import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { SmartImage } from "./ui";

export default function ProjectCard({ project, className = "", cursorFollow = false, onOpen, aspect = "aspect-[4/5]", index = 0 }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0, show: false });

  const handleMove = (e) => {
    if (!cursorFollow || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos({ x: e.clientX - r.left, y: e.clientY - r.top, show: true });
  };

  const cover = project.cover_image || (project.images && project.images[0]?.url);
  const primaryTag = project.tags?.[0] || project.category;

  return (
    <div
      ref={ref}
      data-testid={`project-card-${project.slug}`}
      onMouseMove={handleMove}
      onMouseLeave={() => setPos((p) => ({ ...p, show: false }))}
      className={`group relative overflow-hidden rounded-xl2 border border-line bg-white shadow-soft transition-all duration-500 ease-premium hover:shadow-lift hover:-translate-y-1 ${className}`}
    >
      <button
        type="button"
        onClick={() => onOpen && onOpen(project)}
        className={`relative block w-full ${aspect} cursor-pointer`}
        aria-label={`Preview ${project.name}`}
      >
        <SmartImage src={cover} alt={project.name} w={800} className="" imgClass="transition-transform duration-[900ms] ease-premium group-hover:scale-[1.06]" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-ink/10 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-500" />

        {cursorFollow && pos.show && (
          <span
            className="pointer-events-none absolute z-20 hidden lg:flex items-center gap-1 -translate-x-1/2 -translate-y-1/2 bg-gold text-white text-[11px] font-bold uppercase tracking-wider px-3 py-2 rounded-full shadow-gold"
            style={{ left: pos.x, top: pos.y, transition: "opacity .2s" }}
          >
            View Project <ArrowUpRight size={13} />
          </span>
        )}

        <div className="absolute left-0 right-0 bottom-0 p-4 md:p-5 text-left">
          <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-gold-light mb-1.5">{primaryTag}</span>
          <h3 className="text-white font-semibold text-base md:text-lg leading-tight">{project.name}</h3>
          {project.location && (
            <p className="flex items-center gap-1 text-white/70 text-xs mt-1 opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-400">
              <MapPin size={12} /> {project.location}
            </p>
          )}
        </div>
      </button>

      <Link
        to={`/projects/${project.slug}`}
        data-testid={`project-view-${project.slug}`}
        className="absolute top-3 right-3 z-20 w-10 h-10 rounded-full bg-white/90 text-ink flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-400 hover:bg-gold hover:text-white"
        aria-label={`Open ${project.name} details`}
      >
        <ArrowUpRight size={18} />
      </Link>
    </div>
  );
}
